import { useEffect, useMemo, useState } from 'react';
import {
   collection,
   doc,
   getDoc,
   getDocs,
   limit,
   query,
   where,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type { UploaderProfileLike } from '../components/common/MediaUploaderAttribution';

/** Per-userId profile from Firestore/API (not from media doc denormalization). */
const remoteCache = new Map<string, UploaderProfileLike>();

function profileHasLabel(p: UploaderProfileLike): boolean {
   return Boolean(
      p.displayName?.trim() || p.name?.trim() || p.fullName?.trim()
   );
}

/** Avatar `src` must be a real URL/path; a bare Firebase uid becomes a relative request and 404s. */
function usableAvatarSrc(value: unknown): string | undefined {
   if (value == null || typeof value !== 'string') return undefined;
   const t = value.trim();
   if (!t) return undefined;
   if (/^https?:\/\//i.test(t)) return t;
   if (t.startsWith('data:') || t.startsWith('blob:')) return t;
   if (t.startsWith('/')) return t;
   return undefined;
}

/** Root-only fields for uploader chips; excludes nested `photographerProfile` / portfolio blobs from public API. */
const UPLOADER_CHIP_ROOT_KEYS: readonly string[] = [
   'displayName',
   'display_name',
   'fullName',
   'full_name',
   'firstName',
   'first_name',
   'lastName',
   'last_name',
   'name',
   'photoURL',
   'photoUrl',
   'profilePicture',
   'profile_picture',
   'profilePhoto',
   'avatar',
   'accountType',
];

function pickRootFieldsForUploaderChip(
   raw: Record<string, unknown> | null | undefined
): Record<string, unknown> {
   if (!raw || typeof raw !== 'object') return {};
   const out: Record<string, unknown> = {};
   for (const key of UPLOADER_CHIP_ROOT_KEYS) {
      if (Object.prototype.hasOwnProperty.call(raw, key) && raw[key] !== undefined) {
         out[key] = raw[key];
      }
   }
   return out;
}

/**
 * Normal user profile only (avatar + name) for on-media attribution.
 * Ignores `photographerProfile.*` — photographer branding belongs in the profile popover.
 */
export function normalizeUserProfileForUploaderChip(
   raw: Record<string, unknown> | null | undefined
): UploaderProfileLike {
   return normalizeToUploaderLike(pickRootFieldsForUploaderChip(raw));
}

/** Map Firestore user doc or public API JSON into uploader chip shape (incl. firstName + lastName → label). */
export function normalizeToUploaderLike(
   raw: Record<string, unknown> | null | undefined
): UploaderProfileLike {
   if (!raw || typeof raw !== 'object') return {};
   const o = raw as Record<string, unknown>;
   const first = String(o.firstName ?? o.first_name ?? '').trim();
   const last = String(o.lastName ?? o.last_name ?? '').trim();
   const parts = [first, last].filter(Boolean).join(' ').trim();
   const displayName = String(o.displayName ?? o.display_name ?? '').trim();
   const name = String(o.name ?? '').trim();
   const fullName = String(o.fullName ?? o.full_name ?? '').trim();
   const photoURL = usableAvatarSrc(o.photoURL || o.photoUrl);
   const profilePicture = usableAvatarSrc(
      o.profilePicture || o.profile_picture || o.profilePhoto || o.photoURL || o.photoUrl
   );
   const profilePhoto = usableAvatarSrc(o.profilePhoto);
   const avatar = usableAvatarSrc(o.avatar);
   return {
      displayName: displayName || name || fullName || parts || undefined,
      name: name || undefined,
      fullName: fullName || undefined,
      photoURL,
      profilePicture: profilePicture || photoURL,
      profilePhoto: profilePhoto || undefined,
      avatar: avatar || undefined,
      accountType: Array.isArray(o.accountType) ? (o.accountType as string[]) : undefined,
   };
}

function mergeUploaderLayers(
   base: UploaderProfileLike,
   ...layers: (UploaderProfileLike | null | undefined)[]
): UploaderProfileLike {
   const out: UploaderProfileLike = { ...base };
   for (const layer of layers) {
      if (!layer) continue;
      if (!out.displayName?.trim() && layer.displayName?.trim()) out.displayName = layer.displayName;
      if (!out.name?.trim() && layer.name?.trim()) out.name = layer.name;
      if (!out.fullName?.trim() && layer.fullName?.trim()) out.fullName = layer.fullName;
      if (!out.photoURL && layer.photoURL) out.photoURL = layer.photoURL;
      if (!out.profilePicture && (layer.profilePicture || layer.photoURL))
         out.profilePicture = layer.profilePicture || layer.photoURL;
      if (!out.profilePhoto && layer.profilePhoto) out.profilePhoto = layer.profilePhoto;
      if (!out.avatar && layer.avatar) out.avatar = layer.avatar;
      if ((!out.accountType || !out.accountType.length) && layer.accountType?.length)
         out.accountType = layer.accountType;
   }
   return out;
}

/** When media.userId/uploadedBy is not the profile document id, find by userId/uid field. */
async function fetchFirestoreProfileByField(uid: string): Promise<UploaderProfileLike | null> {
   const cols = ['userProfile', 'userProfiles', 'users'] as const;
   const fields = ['userId', 'uid', 'user_id'] as const;
   for (const col of cols) {
      for (const field of fields) {
         try {
            const q = query(collection(db, col), where(field, '==', uid), limit(1));
            const snap = await getDocs(q);
            if (!snap.empty) {
               const data = snap.docs[0].data() as Record<string, unknown>;
               return normalizeUserProfileForUploaderChip(data);
            }
         } catch {
            // rules / index — try next
         }
      }
   }
   return null;
}

async function fetchFirestoreProfile(uid: string): Promise<UploaderProfileLike | null> {
   try {
      let profileDoc = await getDoc(doc(db, 'userProfile', uid));
      if (!profileDoc.exists()) profileDoc = await getDoc(doc(db, 'userProfiles', uid));
      if (!profileDoc.exists()) profileDoc = await getDoc(doc(db, 'users', uid));
      if (profileDoc.exists() && profileDoc.data()) {
         return normalizeUserProfileForUploaderChip(profileDoc.data() as Record<string, unknown>);
      }
   } catch {
      // ignore
   }
   try {
      return await fetchFirestoreProfileByField(uid);
   } catch {
      return null;
   }
}

async function fetchPublicApiProfile(uid: string): Promise<UploaderProfileLike | null> {
   const base = process.env.REACT_APP_API_URL;
   if (!base) return null;
   try {
      const res = await fetch(`${base}/api/profile/public/${uid}`);
      if (!res.ok) return null;
      const data = (await res.json()) as Record<string, unknown>;
      return normalizeUserProfileForUploaderChip(data);
   } catch {
      return null;
   }
}

/**
 * Resolve uploader chip (avatar + name). If `seed` has a display name (e.g. from GET /albums/public),
 * skips Firestore/public API for the chip — use click + `/api/profile/public` for full profile only.
 */
export function useResolvedUploaderProfile(
   uid: string | undefined,
   seed?: UploaderProfileLike | Record<string, unknown> | null
): { profile: UploaderProfileLike; loading: boolean } {
   const id = String(uid || '').trim();

   const seedChip = useMemo(() => {
      if (seed == null || typeof seed !== 'object') return {} as UploaderProfileLike;
      return normalizeUserProfileForUploaderChip(seed as Record<string, unknown>);
   }, [seed]);

   const seedHasLabel = profileHasLabel(seedChip);

   const [remote, setRemote] = useState<UploaderProfileLike | null>(null);
   const [loading, setLoading] = useState(false);

   useEffect(() => {
      if (!id) {
         setRemote(null);
         setLoading(false);
         return;
      }

      if (seedHasLabel) {
         setRemote(null);
         setLoading(false);
         remoteCache.set(id, seedChip);
         return;
      }

      const cached = remoteCache.get(id);
      if (cached && profileHasLabel(cached)) {
         setRemote(cached);
         setLoading(false);
         return;
      }

      let cancelled = false;
      setLoading(true);
      setRemote(null);

      (async () => {
         const fs = await fetchFirestoreProfile(id);
         if (cancelled) return;

         let fetched: UploaderProfileLike = fs ? { ...fs } : {};
         if (!profileHasLabel(fetched)) {
            const api = await fetchPublicApiProfile(id);
            if (!cancelled) fetched = mergeUploaderLayers(fetched, api || {});
         }

         if (cancelled) return;

         const plainRemote = mergeUploaderLayers({}, fetched);
         if (profileHasLabel(plainRemote)) {
            remoteCache.set(id, plainRemote);
         }
         setRemote(fetched);
         setLoading(false);
      })();

      return () => {
         cancelled = true;
      };
   }, [id, seedHasLabel, seedChip]);

   const profile = useMemo(() => {
      if (seedHasLabel) return seedChip;
      return mergeUploaderLayers(seedChip, remote ?? {});
   }, [seedHasLabel, seedChip, remote]);

   const loadingOut = seedHasLabel ? false : loading;
   return { profile, loading: loadingOut };
}
